/* Complete PC prices using the same public retail contributions as the product configurator. */
((scope) => {
  'use strict';
  const componentKeys = ['case', 'mobo', 'cpu', 'ram', 'gpu', 'psu', 'cooler', 'wifi',
    'sata_ssd', 'sata_hdd', 'nvme_ssd', 'windows_key', 'kettle_lead'];
  const coreKeys = ['case', 'mobo', 'cpu', 'ram', 'gpu', 'psu'];
  const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const validAmount = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const optionFor = (catalog, key, sku) => Object.hasOwn(catalog?.componentOptions?.[key] || {}, sku)
    ? catalog.componentOptions[key][sku] : null;
  const privatePending = new Map(), privateFailed = new Map(), privateInFlight = new Set();
  let privateScheduled = false, privateActive = 0, privateContext = '';
  const selectionKey = parts => JSON.stringify(Object.entries(parts || {}).sort(([a],[b])=>a.localeCompare(b)));
  const comparable = parts => selectionKey({...parts,case:''});
  const publicMoney = value => value?.currency === 'GBP' && Number.isFinite(value.amount) && value.amount > 0
    ? {...value,pennies:Math.round(value.amount*100),formatted:formatter.format(value.amount)} : null;
  function privatePrice(selections) {
    const context = comparable(selections);
    if (privateContext !== context) { privateContext = context; privatePending.clear(); }
    const cached = scope.ElitePCPagePrice?.estimate({selections,accessories:[]})
      || scope.ElitePCPrivate?.peekPrice(selections,[]);
    if (cached) return publicMoney(cached);
    const key = selectionKey(selections);
    if ((privateFailed.get(key)||0)>Date.now() || privatePending.has(key) || privateInFlight.has(key) || !selections?.case) return null;
    privatePending.set(key,{...selections});
    if (!privateScheduled) { privateScheduled=true; scope.setTimeout(runPrivatePrices,0); }
    return null;
  }
  async function runPrivatePrices() {
    privateScheduled = false;
    if (privateActive >= 2) return;
    const first = privatePending.entries().next().value;
    if (!first) return;
    const batch = [...privatePending].filter(([,parts])=>comparable(parts)===comparable(first[1])).slice(0,12);
    for(const [key] of batch){privatePending.delete(key);privateInFlight.add(key);}
    privateActive++;
    if(privatePending.size && privateActive<2) {privateScheduled=true;scope.setTimeout(runPrivatePrices,0);}
    const controller=new AbortController(),timer=scope.setTimeout(()=>controller.abort(),15000);
    try {
      await (scope.ElitePCPrivate.prices || scope.ElitePCPrivate.fit)({selections:first[1],accessories:[],component:'case',skus:batch.map(([,parts])=>parts.case)}, {signal:controller.signal});
      for (const [key,parts] of batch) if (!scope.ElitePCPrivate.peekPrice(parts,[])) privateFailed.set(key,Date.now()+30000);
    } catch { for (const [key] of batch) privateFailed.set(key,Date.now()+30000); }
    finally {
      scope.clearTimeout(timer);for (const [key] of batch) privateInFlight.delete(key);
      privateActive--;
      scope.document?.dispatchEvent(new CustomEvent('elite:private-prices:updated'));
      if (privatePending.size && !privateScheduled) {privateScheduled=true;scope.setTimeout(runPrivatePrices,0);}
    }
  }

  const pageSources = new WeakMap();
  function readPage(source) {
    if (!source) return null;
    const text = source.textContent || 'null';
    const previous = pageSources.get(source);
    if (previous?.text === text) return previous.catalog;
    try {
      let catalog = JSON.parse(text);
      if (typeof catalog === 'string') catalog = JSON.parse(catalog);
      catalog = fromPage(catalog);
      pageSources.set(source, { text, catalog });
      return catalog;
    } catch { return null; }
  }

  // Native prices resolve at render time. Draft-only retail comes from the signed, event-updated public mirror.
  function fromPage(catalog) {
    if (catalog?.pricingMode === 'private_retail') {
      catalog.displayPricing = {private:true};
      catalog.priceState = 'ready';
      return catalog;
    }
    if (catalog?.priceState !== 'page') return catalog;
    const current = catalog.variantPrices;
    const policy = scope.ElitePCPagePrice?.policy;
    if (policy?.version === 1 && policy.currency === 'GBP') {
      catalog.defaultSelections = { ...policy.defaultSelections };
      catalog.requiredComponents = policy.requiredComponents;
      catalog.displayPricing = { ...policy.displayPricing };
    }
    const hidden = catalog.hiddenPrices;
    const valid = current?.currency === 'GBP' && current.bySku && typeof current.bySku === 'object';
    // Reuse the listing's existing native-price payload instead of resolving
    // every variant a second time in the page head.
    if (valid && scope.ElitePCPagePrice?.nativePrices) Object.assign(scope.ElitePCPagePrice.nativePrices,current.bySku);
    if (valid) catalog.caseCards = Object.keys(catalog.componentOptions?.case || {}).flatMap(sku => {
      const row = current.bySku[sku], card = row?.[3];
      return card?.id && card.url?.startsWith('/products/')
        ? [{ ...card, sku, pennies: row[0], available: row[1] === true }] : [];
    });
    let count = 0;
    for (const options of Object.values(catalog.componentOptions || {})) {
      for (const [sku, option] of Object.entries(options)) {
        const variant = valid && Object.hasOwn(current.bySku, sku) ? current.bySku[sku] : null;
        const pennies = Array.isArray(variant) ? variant[0]
          : valid && hidden?.version === 1 && hidden.currency === 'GBP' && Object.hasOwn(hidden.bySku || {},sku) ? hidden.bySku[sku] : null;
        option.priceContribution = Number.isSafeInteger(pennies) && pennies >= 0 ? pennies / 100 : null;
        if (option.priceContribution !== null) count++;
        if (typeof variant?.[1] === 'boolean') option.available = variant[1];
        // This exact variant photo is available with the initial HTML, including
        // RAM/PSU models that are grouped under another SKU in the sidebar.
        option.image = typeof variant?.[2] === 'string' && /^(https:)?\/\//i.test(variant[2]) ? variant[2] : '';
      }
    }
    catalog.priceState = count ? 'ready' : 'pending';
    catalog.priceSource = count ? 'shopify-page' : null;
    delete catalog.variantPrices;
    delete catalog.hiddenPrices;
    return catalog;
  }

  const pageRequests = new WeakMap();
  // Draft-only components cannot be read through Liquid. One small, fresh quote
  // fills their contribution; loading every part and its specifications is unnecessary.
  function resolvePage(catalog) {
    if(scope.ElitePCS?.privatePricing) return Promise.resolve(false);
    catalog = fromPage(catalog);
    if (!catalog || catalog.priceSource !== 'shopify-page') return Promise.resolve(false);
    if (pageRequests.has(catalog)) return pageRequests.get(catalog);
    const selections = { ...catalog.defaultSelections };
    const missing = Object.entries(selections).filter(([key, sku]) => !validAmount(optionFor(catalog, key, sku)?.priceContribution));
    const rules = catalog.displayPricing;
    if (missing.length !== 1 || rules.baseAmount !== 0 || rules.roundTo !== 0 || rules.ending !== 'none'
      || rules.minimum !== 0 || rules.maximum !== 0) return Promise.resolve(false);
    catalog.pagePricePending = true;
    const fetchQuote = async () => {
      // Start this deadline after any preloaded request has finished.
      const controller = new AbortController();
      const timeout = scope.setTimeout(() => controller.abort(), 12000);
      try {
        const response = await (scope.ElitePCQuoteTransport?.fetch || scope.fetch)('/apps/pc-configurator?action=validate', {
          method: 'POST', cache: 'no-store', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ selections, accessories: [], checkout: false })
        });
        // A stock/compatibility conflict can still include a verified retail
        // price. It remains blocked for checkout; only the price is used here.
        return response.ok || response.status === 409 ? await response.json() : null;
      } finally { scope.clearTimeout(timeout); }
    };
    const request = Promise.resolve(scope.ElitePCCollectionQuote?.take(selections, catalog))
      .then(prefetched => prefetched || fetchQuote()).then(result => {
      const quote = result?.price;
      if (result?.success !== true || quote?.currency !== 'GBP' || !validAmount(quote.amount)
        || !validAmount(quote.additionAmount) || !validAmount(quote.componentProfit?.amount)
        || quote.accessoryProfit?.amount !== 0) return false;
      const knownPennies = Object.entries(selections).reduce((total, [key, sku]) =>
        total + Math.round((optionFor(catalog, key, sku)?.priceContribution || 0) * 100), 0);
      const pennies = Math.round(quote.amount * 100) - Math.round(quote.additionAmount * 100)
        - Math.round(quote.componentProfit.amount * 100) - knownPennies;
      if (!Number.isSafeInteger(pennies) || pennies < 0) return false;
      const [key, sku] = missing[0];
      optionFor(catalog, key, sku).priceContribution = pennies / 100;
      if (result.build?.allComponentsInStock === true) optionFor(catalog, key, sku).available = true;
      rules.additionAmount = quote.additionAmount;
      if (Number.isSafeInteger(quote.componentProfit.componentCount) && quote.componentProfit.componentCount > 0) {
        rules.componentAdditionAmount = quote.componentProfit.amount / quote.componentProfit.componentCount;
      }
      return true;
    }).catch(() => false).finally(() => {
      catalog.pagePricePending = false;
    });
    pageRequests.set(catalog, request);
    return request;
  }

  function project(catalog) {
    if (catalog?.pricingMode === 'private_retail' && Array.isArray(catalog.componentGroups)) {
      scope.ElitePCBudgetBuilds?.setCatalog(catalog);
      const componentOptions={};
      for(const group of catalog.componentGroups) {
        componentOptions[group.key]=Object.fromEntries((group.options||[]).map(option=>[option.sku,{
          priceOrder:option.priceOrder,available:option.availability?.available===true,
          image:option.images?.[0] || '',
          ...(group.key==='cpu'?{brand:/\b(intel|core|ultra)\b/i.test(option.label)?'intel':/\b(amd|ryzen)\b/i.test(option.label)?'amd':''}: {})
        }]));
      }
      return {pricingMode:'private_retail',displayPricing:{private:true},componentOptions,
        defaultSelections:catalog.defaultSelections||{},requiredComponents:catalog.requiredComponents||coreKeys,priceState:'ready'};
    }
    const rules = catalog?.displayPricing;
    if (catalog?.success !== true || !Array.isArray(catalog.componentGroups) || rules?.version !== 1
      || rules.currency !== 'GBP' || !['ceil', 'floor', 'nearest'].includes(rules.roundMode)
      || !['99', '95', 'none'].includes(rules.ending)
      || !['baseAmount', 'roundTo', 'minimum', 'maximum'].every(key => validAmount(rules[key]))) return null;
    scope.ElitePCBudgetBuilds?.setCatalog(catalog);
    const displayPricing = Object.fromEntries(['version', 'currency', 'baseAmount', 'roundTo', 'roundMode',
      'ending', 'minimum', 'maximum'].map(key => [key, rules[key]]));
    displayPricing.additionAmount = scope.ElitePCPriceAddition?.resolveAmount(rules.additionAmount) ?? 0;
    displayPricing.componentAdditionAmount = scope.ElitePCPriceAddition?.resolveComponentAmount(rules.componentAdditionAmount) ?? 0;
    const componentOptions = {};
    for (const group of catalog.componentGroups) {
      if (!componentKeys.includes(group?.key) || group.visible === false || group.sendToShopify === false) continue;
      const options = Object.create(null);
      for (const option of group.options || []) {
        if (!option?.sku || option.visible === false || option.sendToShopify === false) continue;
        options[option.sku] = {
          priceContribution: validAmount(option.priceContribution) ? option.priceContribution : null,
          available: option.availability?.available === true && (option.availability?.quantity == null || option.availability.quantity > 0)
        };
        if (group.key === 'cpu') {
          const brandText = [option.label, option.meta?.socketType,
            ...(option.displaySpecs || []).filter(spec => /socket/i.test(spec.label)).map(spec => spec.value)].join(' ');
          options[option.sku].brand = /\b(?:intel|core|ultra|lga\s*\d+)\b/i.test(brandText) ? 'intel'
            : /\b(?:amd|ryzen|am[345])\b/i.test(brandText) ? 'amd' : '';
        }
      }
      componentOptions[group.key] = options;
    }
    const defaultSelections = Object.fromEntries(Object.entries(catalog.defaultSelections || {})
      .filter(([key, sku]) => componentKeys.includes(key) && typeof sku === 'string' && sku));
    const requiredComponents = [...new Set([...coreKeys, ...(catalog.requiredComponents || [])])];
    return { displayPricing, defaultSelections, requiredComponents, componentOptions, priceState: 'ready' };
  }

  function configuration(catalog, caseSku, entries = [], context = {}) {
    catalog = fromPage(catalog);
    if (!catalog?.displayPricing || !caseSku) return null;
    // Never substitute default parts when a model landing link cannot be resolved.
    if (context.componentUnavailable || context.processorUnavailable) return null;
    const selectedBudgets = context.budgets || scope.ElitePCBudgetBuilds?.selectedRanges() || [];
    const prepared=scope.ElitePCPrepared?.choose(caseSku,entries,context,selectedBudgets);
    if(prepared!==undefined)return prepared;
      // Category browsing waits for a fresh checked publication. It must not
      // fall back to an unrestricted default GPU while that publication loads.
      if((context.gpuFamily==='nvidia-rtx'||context.processorBrand) && !entries.some(entry=>/^(?:elite_|component_)(?:mobo|cpu|ram|gpu|psu|cooler|wifi|sata_ssd|nvme_ssd|sata_hdd)$/.test(entry.param)&&entry.value&&!/^__no_/.test(entry.value))) {
        return {selections:{},price:null,priceState:'pending'};
      }
    const needsCompleteBuild = catalog.pricingMode === 'private_retail'
      && (entries.some(entry => entry.value && !/^__no_/.test(entry.value)) || context.processorBrand);
    if (selectedBudgets.length || needsCompleteBuild) {
      // A model/category change may also require a different platform. Ask for
      // a whole checked build instead of pricing a CPU in the old motherboard.
      const ranges = selectedBudgets.length ? selectedBudgets : ['under-800','800-1000','1000-1500','1500-2000','2000-plus'];
      const build=scope.ElitePCBudgetBuilds?.get(catalog, caseSku, entries, {...context,categorySearch:!selectedBudgets.length}, ranges) || null;
      const checked=build && scope.ElitePCEntry?.displaySelection(build.selections,[]);
      return checked ? {...build,selections:{...checked.selection.selections},price:publicMoney(checked.price),priceState:'ready'} : build;
    }
    scope.ElitePCBudgetBuilds?.cancel();
    const selections = { ...catalog.defaultSelections, case: caseSku };
    for (const entry of entries) {
      const key = String(entry.param || '').replace(/^(?:elite_|component_)/, '');
      if ((key === 'sata_hdd' && entry.value === '__no_hdd__') ||
          (key === 'sata_ssd' && entry.value === '__no_sata_ssd__')) {
        delete selections[key];
        continue;
      }
      if (key !== 'case' && componentKeys.includes(key) && entry.value) selections[key] = entry.value;
    }
    // When only a processor brand is chosen, keep a matching default CPU.
    // An explicit CPU selection always wins, including saved configurations.
    if (['amd', 'intel'].includes(context.processorBrand) && !entries.some(entry => /^(?:elite_|component_)cpu$/.test(entry.param))) {
      if (optionFor(catalog, 'cpu', selections.cpu)?.brand !== context.processorBrand) {
        const candidates = Object.entries(catalog.componentOptions.cpu || {}).filter(([, option]) =>
          option.brand === context.processorBrand && validAmount(option.priceOrder ?? option.priceContribution));
        const available = candidates.filter(([, option]) => context.stockMode === 'all'
          || (context.stockMode === 'out_of_stock' ? !option.available : option.available));
        const candidate = available.sort((left, right) => (left[1].priceOrder ?? left[1].priceContribution) - (right[1].priceOrder ?? right[1].priceContribution))[0];
        if (!candidate) return null;
        selections.cpu = candidate[0];
      }
    }
    const safe=scope.ElitePCEntry?.displaySelection(selections,[]);
    if(safe)return {selections:{...safe.selection.selections},price:publicMoney(safe.price),priceState:'ready'};
    const total=price(catalog,selections);
    return { selections, price:total, priceState:catalog.pricingMode==='private_retail' && !total ? 'pending' : catalog.pagePricePending ? 'pending' : catalog.priceState || 'ready' };
  }

  function price(catalog, selections) {
    catalog = fromPage(catalog);
    if (catalog?.pricingMode === 'private_retail') return privatePrice(selections);
    const rules = catalog?.displayPricing;
    // Contributions come from this page's native Shopify prices or a fresh catalogue.
    if (!rules || (catalog.priceState && catalog.priceState !== 'ready')
      || catalog.requiredComponents.some(key => !selections?.[key])) return null;
    let total = rules.baseAmount;
    for (const [key, sku] of Object.entries(selections)) {
      const contribution = optionFor(catalog, key, sku)?.priceContribution;
      if (!validAmount(contribution)) return null;
      total += contribution;
    }
    // Match displayPrice() on the PC product page, including its two-stage rounding.
    total = Math.round(Math.round(total * 1e8) / 1e6) / 100;
    if (rules.roundTo > 0) {
      const round = rules.roundMode === 'nearest' ? Math.round : rules.roundMode === 'floor' ? Math.floor : Math.ceil;
      total = round(total / rules.roundTo) * rules.roundTo;
    }
    if (total >= 1) total -= rules.ending === '99' ? 0.01 : rules.ending === '95' ? 0.05 : 0;
    if (rules.minimum > 0) total = Math.max(total, rules.minimum);
    if (rules.maximum > 0) total = Math.min(total, rules.maximum);
    const additionAmount = scope.ElitePCPriceAddition?.resolveAmount(rules.additionAmount) ?? 0;
    const componentProfit = scope.ElitePCPriceAddition?.componentSummary(selections, rules.componentAdditionAmount);
    let pennies = Math.round(Number(total.toFixed(2)) * 100) + Math.round(additionAmount * 100)
      + Math.round((componentProfit?.amount || 0) * 100);
    const tierPrice = scope.ElitePCBuildPricing?.price((pennies - Math.round(additionAmount * 100)) / 100);
    if (scope.ElitePCBuildPricing && !tierPrice) return null;
    if (tierPrice) pennies = tierPrice.pennies;
    if (!Number.isSafeInteger(pennies) || pennies <= 0) return null;
    return { amount: pennies / 100, pennies, currency: 'GBP', formatted: formatter.format(pennies / 100) };
  }

  function budgetValues(pennies) {
    if (!Number.isSafeInteger(pennies) || pennies <= 0) return [];
    const ranges = [
      ['under-800', 0, 80000], ['800-1000', 80000, 100000],
      ['1000-1500', 100000, 150000], ['1500-2000', 150000, 200000],
      ['2000-plus', 200000, Infinity], ['2500-plus', 250000, Infinity],
      ['3000-plus', 300000, Infinity], ['800-1200', 80000, 120000],
      ['1200-1800', 120000, 180000], ['1800-2500', 180000, 250000]
    ];
    return ranges.filter(([, minimum, maximum]) => pennies >= minimum && pennies < maximum).map(([key]) => key);
  }

  function paint(card, build) {
    if (!card) return;
    scope.ElitePCBudgetBuilds?.paint(card, build);
    const value = build?.price;
    card.dataset.sortPrice = value ? String(value.pennies) : 'NaN';
    card.dataset.eliteFullPcPrice = value ? String(value.pennies) : '';
    card.dataset.filterBudget = budgetValues(value?.pennies).join('|');
    const pricing = card.querySelector('[data-elite-pc-card-pricing]');
    if (!pricing) return;
    const current = pricing.querySelector('.epc-spotlight__price');
    const compare = pricing.querySelector('.epc-spotlight__compare');
    const saving = pricing.querySelector('.epc-spotlight__saving');
    const state = value ? 'ready' : build?.priceState === 'pending' ? 'pending' : 'unavailable';
    // A budget search may still be finding a different build. Keep an existing
    // total visible until its replacement arrives, without a calculation label.
    const text = value?.formatted || (state === 'pending' ? (current?.textContent?.trim().startsWith('£') ? current.textContent : '') : 'Check price');
    // Avoid unnecessary DOM mutations: the sidebar observes replacement cards.
    if (current && current.textContent !== text) current.textContent = text;
    const label=value ? `Full PC price ${text}` : state==='pending' ? 'Updating full PC price' : text;
    if(current?.getAttribute('aria-label')!==label)current?.setAttribute('aria-label',label);
    if(pricing.getAttribute('aria-busy')!==String(state==='pending'))pricing.setAttribute('aria-busy',String(state==='pending'));
    pricing.dataset.elitePcPriceState = state;
    if (value && !pricing.dataset.elitePcPriceReadyAt) pricing.dataset.elitePcPriceReadyAt = String(Math.round(scope.performance?.now?.() || 0));
    if (value && build.autoBudget && card.isConnected && !document.documentElement.dataset.eliteBudgetFirstPriceMs) {
      document.documentElement.dataset.eliteBudgetFirstPriceMs = String(Math.round(scope.performance?.now?.() || 0));
    }
    if(!value && state==='pending')return;
    // Each selection is a different complete PC. Do not invent a previous
    // selling price by adding an amount or percentage to its current total.
    for(const node of [compare,saving])if(node){node.hidden=true;if(node.textContent)node.textContent='';}
  }

  const api = { readPage, fromPage, resolvePage, project, configuration, price, paint, budgetValues };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.EliteCollectionPCPrices = api;
})(typeof window === 'object' ? window : {});
